import axios from 'axios';

import type { Address, UserDetailsShowResponse } from '@/types/api/user/details/show';
import type { UserDetailsUpdateRequest } from '@/types/api/user/details/update';
import type { UserSubscription } from '@/types/api/user/subscriptions';

export const axiosInstance = axios.create({
  // baseURL: `${import.meta.env.PUBLIC_API_URL || '/api'}`,
  baseURL: '/api',
  timeout: 10000,
});

export const api = {
  auth: {
    login: (credentials: { email: string; password: string }) => {
      const formData = new FormData();
      formData.append('email', credentials.email);
      formData.append('password', credentials.password);

      return axiosInstance.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    signupInitiate: (email: string, referringSite?: string) => {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('referringSite', referringSite || '');

      return axiosInstance.post('/signup', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    signupComplete: (credentials: { emailId: string; password: string }) => {
      const formData = new FormData();
      formData.append('emailId', credentials.emailId);
      formData.append('password', credentials.password);

      return axiosInstance.post('/signup/complete', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    checkoutSignup: (data: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      billingAddress: Address;
      shippingAddress: Address;
    }) => {
      const formData = new FormData();
      formData.append('email', data.email);
      formData.append('password', data.password);
      formData.append('firstName', data.firstName);
      formData.append('lastName', data.lastName);
      formData.append('billingAddress.firstName', data.billingAddress.firstName);
      formData.append('billingAddress.lastName', data.billingAddress.lastName);
      formData.append('billingAddress.companyName', data.billingAddress.company || '');
      formData.append('billingAddress.street', data.billingAddress.addressLine1);
      formData.append('billingAddress.aptNumber', data.billingAddress.addressLine2 || '');
      formData.append('billingAddress.city', data.billingAddress.city);
      formData.append('billingAddress.state', data.billingAddress.state);
      formData.append('billingAddress.zip', data.billingAddress.zip);
      formData.append('billingAddress.country', data.billingAddress.country);
      formData.append('shippingAddress.firstName', data.shippingAddress.firstName);
      formData.append('shippingAddress.lastName', data.shippingAddress.lastName);
      formData.append('shippingAddress.companyName', data.shippingAddress.company || '');
      formData.append('shippingAddress.street', data.shippingAddress.addressLine1);
      formData.append('shippingAddress.aptNumber', data.shippingAddress.addressLine2 || '');
      formData.append('shippingAddress.city', data.shippingAddress.city);
      formData.append('shippingAddress.state', data.shippingAddress.state);
      formData.append('shippingAddress.zip', data.shippingAddress.zip);
      formData.append('shippingAddress.country', data.shippingAddress.country);

      return axiosInstance.post('/checkout/signup', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    checkoutSessionCreate: (lineItems: { price: string; quantity: number }[], clientReferenceId: string) => {
      return axiosInstance.post('/stripe/checkout/sessions/create', { lineItems, clientReferenceId });
    },
    checkoutSessionRetrieveLineItems: (sessionId: string) => {
      return axiosInstance.get('/stripe/checkout/sessions/retrieveLineItems', { params: { sessionId } });
    },
    userShow: () => axiosInstance.get('/user/show'),
    logout: () => axiosInstance.post('/auth/logout'),
    magazine: {
      access: (userId: string, issueId: string) => {
        return axiosInstance.get('/magazine/access/show', { params: { userId, issueId } });
      },
    },
    user: {
      details: {
        update: (data: UserDetailsUpdateRequest) => axiosInstance.put('/user/details/update', data),
        show: () => axiosInstance.get<UserDetailsShowResponse>('/user/details/show'),
      },
      password: {
        update: (data: { oldPassword: string; newPassword: string }) =>
          axiosInstance.put('/user/password/update', data),
      },
    },
    users: {
      show: (userId: string) => axiosInstance.get(`/users/${userId}`),
      subscriptions: {
        index: (userId: string) => axiosInstance.get<UserSubscription[]>(`/users/${userId}/subscriptions`),
        delete: (userId: string, subscriptionId: string) =>
          axiosInstance.delete(`/users/${userId}/subscriptions/${subscriptionId}`),
      },
    },
    stripe: {
      paymentMethods: {
        update: (paymentMethodId: string) => axiosInstance.patch('/stripe/payment-methods/update', { paymentMethodId }),
      },
      subscriptions: {
        delete: () => axiosInstance.delete('/stripe/subscriptions/delete'),
        show: () => axiosInstance.get('/stripe/subscriptions/show'),
      },
    },
  },
  passwordReset: {
    session: {
      create: (email: string) => axiosInstance.post('/password-reset/session', { email }),
    },
    verifyEmail: (verificationCode: string) => axiosInstance.post('/password-reset/verify-email', { verificationCode }),
    update: (password: string) => axiosInstance.post('/password-reset/update-password', { password }),
  },
  search: {
    search: (params: { query: string; page: number; limit?: number }) => axiosInstance.get('/search', { params }),
  },
  relatedSearch: {
    relatedSearch: (params: { similarQuery: string; page: number; limit?: number }) =>
      axiosInstance.get('/related-search', { params }),
  },
  partnerPromotion: {
    partnerPromotion: (data: {
      isPrint: boolean;
      email: string;
      firstName: string;
      lastName: string;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state: string;
      zip?: string;
      interests: string[];
      referringPartner: string;
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
      referringSite?: string;
    }) => axiosInstance.post('/partner-promotion', data),
  },
  curated: {
    post: (data: { distinctUserId: string }) => axiosInstance.post('/curated', data),
  },
};
